import { Node, nodeInputRule } from '@tiptap/react';
import { Plugin, PluginKey } from 'prosemirror-state';
import { identityTiptapFileSrcResolver, type TiptapFileSrcResolver } from '../../useTiptapFileSrc';

export interface VideoOptions {
  HTMLAttributes: Record<string, any>;
  resolveFileSrc: TiptapFileSrcResolver;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      /**
       * Set a video node
       */
      setVideo: (
        options: {
          src: string;
          fileDrn?: string;
        },
        position: number,
      ) => ReturnType;
    };
  }
}

const inputRegex = /!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/;

export const Video = Node.create<VideoOptions>({
  name: 'video',

  group: 'block',

  addOptions() {
    return {
      HTMLAttributes: {},
      resolveFileSrc: identityTiptapFileSrcResolver,
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (el) => (el as HTMLSpanElement).getAttribute('src'),
        renderHTML: (attrs) => ({ src: attrs.src }),
      },
      fileDrn: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-file-drn'),
        renderHTML: (attributes) =>
          attributes.fileDrn ? { 'data-file-drn': attributes.fileDrn } : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'video',
        getAttrs: (el) => ({
          src: (el as HTMLVideoElement).getAttribute('src'),
          fileDrn: (el as HTMLVideoElement).getAttribute('data-file-drn'),
        }),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { src, fileDrn } = node.attrs;
    const resolvedSrc = typeof src === 'string' ? this.options.resolveFileSrc(fileDrn, src) : src;
    const resolvedAttrs = { ...HTMLAttributes, src: resolvedSrc };
    return [
      'video',
      { controls: 'true', style: 'width: 100%', ...resolvedAttrs },
      ['source', resolvedAttrs],
    ];
  },

  addCommands() {
    return {
      setVideo:
        (options, position) =>
        ({ commands }) =>
          commands.insertContentAt(position, {
            type: 'video',
            attrs: options,
          }),
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: (match) => {
          const [, , src] = match;

          return { src };
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('videoDropPlugin'),

        props: {
          handleDOMEvents: {
            drop(view, event) {
              const {
                state: { schema, tr },
                dispatch,
              } = view;
              const hasFiles =
                event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length;

              if (!hasFiles) {
                return false;
              }

              const videos = Array.from(event.dataTransfer.files).filter((file) =>
                /video/i.test(file.type),
              );

              if (videos.length === 0) {
                return false;
              }

              event.preventDefault();

              const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });

              videos.forEach((video) => {
                const reader = new FileReader();

                reader.onload = (readerEvent) => {
                  const node = schema?.nodes?.video?.create({ src: readerEvent.target?.result });

                  if (node && coordinates && typeof coordinates.pos === 'number') {
                    const transaction = tr.insert(coordinates?.pos, node);

                    dispatch(transaction);
                  }
                };

                reader.readAsDataURL(video);
              });

              return true;
            },
          },
        },
      }),
    ];
  },
});
